// DUPLICATED from town-square/src/components/InviteFriendModal.tsx — edit both or neither. The
// client cannot import from the town-square workspace, so /network and /notifications here render
// their own copy. Keep the give/get copy, the GraphQL ops, and the share kit identical to the
// town-square copy. Differences are only the framework seams: Clerk (@clerk/clerk-react), the base
// URL (__TOWN_SQUARE_URL__), analytics (useAnalytics().amplitude), and the RedeemShowcase shelf —
// town-square-only — is omitted here.

import { useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useUser } from '@clerk/clerk-react';
import confetti from 'canvas-confetti';
import qrcode from 'qrcode-generator';
import { Check, ChevronDown, Copy, Mail, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input } from '@artstorefronts/ui';
import { inviteCopy, subscriptionAllowanceExhausted } from '@arthelper/shared/referral-invite-copy';
import {
  buildInviteLinkFor,
  buildInviteMessage,
  buildReferralLink,
  type InvitePromo,
  canShareNative,
  shareNative,
} from '@arthelper/shared/referral-share';
import { useReferralSettings } from '~/hooks/useReferralSettings';
import { useAnalytics } from '~/context/analytics';

const MY_REFERRAL_STATS = gql`
  query MyReferralStats {
    myReferralStats {
      friendsJoined
      pointsEarned
    }
  }
`;

// Exported for the sidebar's "N left" badge. Share the doc rather than write a narrower one:
// the allowance has no cache key, so a `{ remaining }`-only write would replace this object
// wholesale and send the modal back to the network.
export const MY_GIFT_ALLOWANCE = gql`
  query MyGiftSubscriptionAllowance {
    myGiftSubscriptionAllowance {
      limit
      used
      remaining
    }
  }
`;

const CREATE_GIFT_INVITE = gql`
  mutation CreateGiftInvite($recipientEmail: String!) {
    createGiftInvite(recipientEmail: $recipientEmail) {
      limit
      used
      remaining
    }
  }
`;

/**
 * INVITE A FRIEND — the one modal every surface opens, built to the proven give/get
 * share kit (Wise/Monzo/PayPal, and every trending Mobbin invite screen):
 *
 *   - a pre-written personal message with the link embedded — never a bare URL;
 *   - the NATIVE SHARE SHEET as the primary action wherever `navigator.share`
 *     exists (one tap into SMS/WhatsApp/IG on mobile);
 *   - a single Copy-link fallback (with confetti) — copy goes anywhere;
 *   - a QR toggle for in-person sharing;
 *   - a numbered "How it works", collapsed by default (progressive disclosure);
 *   - the Monzo-style earned tracker, shown even at zero.
 *
 * THE COPY LEADS WITH THE GIFT (Nick, 2026-08-31): the friend receives the live referral
 * amount FROM HER the moment they join — the benevolent half is the reason to share.
 *
 * `source` names the surface that opened it; `promo` is optional promo context.
 */
export function InviteFriendModal({
  open,
  onOpenChange,
  source,
  promo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: string;
  /** What the invite promotes — the link lands on its public page, the message names it. */
  promo?: InvitePromo;
}) {
  const { user, isLoaded } = useUser();
  const { amplitude } = useAnalytics();
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showHow, setShowHow] = useState(false);

  const link = useMemo(() => {
    const asfId = user?.publicMetadata?.asfId as string | undefined;
    // Invite links always land on town-square (shared helper takes the base per app).
    const base = __TOWN_SQUARE_URL__ || 'https://www.arthelper.com';
    // A promo invite opens the promoted thing's own public landing with signup
    // embedded — the friend sees WHAT they're joining and is fulfilled server-side.
    return promo ? buildInviteLinkFor(asfId, base, promo) : buildReferralLink(asfId, base);
  }, [user, promo]);
  const nativeShare = useMemo(() => canShareNative(), []);

  const referralSettings = useReferralSettings();

  // Only artists giving a subscription have an allowance to show — skip the query otherwise.
  const { data: allowanceData, refetch: refetchAllowance } = useQuery<{
    myGiftSubscriptionAllowance: { limit: number; used: number; remaining: number };
  }>(MY_GIFT_ALLOWANCE, {
    skip: !open || !user || !referralSettings.grantsSubscription,
    fetchPolicy: 'cache-and-network',
  });
  const allowance = allowanceData?.myGiftSubscriptionAllowance ?? null;

  // A subscription-gifting mechanism where THIS artist has spent every gift falls back to
  // points server-side, so the copy must too — otherwise it keeps promoting a Human-Made gift
  // they can no longer give. Exhausted → points-only headline + a notice at the top.
  const subscriptionExhausted = subscriptionAllowanceExhausted(referralSettings, allowance);
  const copy = useMemo(
    () => inviteCopy(subscriptionExhausted ? { ...referralSettings, grantsSubscription: false } : referralSettings),
    [referralSettings, subscriptionExhausted]
  );
  const message = useMemo(() => (link ? buildInviteMessage(link) : ''), [link]);

  const [giftEmail, setGiftEmail] = useState('');
  // Refetch the referrals list on every gift send, so any surface stays fresh.
  const [createGiftInvite, { loading: sendingGift }] = useMutation(CREATE_GIFT_INVITE, {
    refetchQueries: ['MyReferrals'],
  });

  const sendGift = async () => {
    const email = giftEmail.trim();
    if (!email) return;
    try {
      await createGiftInvite({ variables: { recipientEmail: email } });
      setGiftEmail('');
      toast.success(`Gift sent to ${email}`);
      amplitude.track('sn_gift_invite_sent', { source });
      await refetchAllowance();
    } catch (err: any) {
      toast.error(err?.message || 'Could not send that gift');
    }
  };

  const { data: statsData } = useQuery<{ myReferralStats: { friendsJoined: number; pointsEarned: number } }>(
    MY_REFERRAL_STATS,
    { skip: !open || !user, fetchPolicy: 'cache-and-network' }
  );
  const stats = statsData?.myReferralStats;

  // The QR is pure geometry — no network, renders as a crisp SVG at any size.
  const qrModules = useMemo(() => {
    if (!showQr || !link) return null;
    const qr = qrcode(0, 'M');
    qr.addData(link);
    qr.make();
    const count = qr.getModuleCount();
    const modules: { x: number; y: number }[] = [];
    for (let y = 0; y < count; y += 1) {
      for (let x = 0; x < count; x += 1) {
        if (qr.isDark(y, x)) modules.push({ x, y });
      }
    }
    return { count, modules };
  }, [showQr, link]);

  if (!isLoaded || !link) return null;

  const share = (method: string) => {
    amplitude.track('sn_referral_share', {
      method,
      source,
      ...(promo ? { promo_kind: promo.kind, quest: promo.kind === 'quest' ? promo.slug : undefined } : {}),
    });
  };

  // The single share action under the native sheet: full-width so it reads as the clear
  // fallback, outlined so it doesn't compete with the green "Share invite" above it.
  const copyButton =
    'inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90dvh] gap-3 overflow-y-auto rounded-[22px] border border-black/5 bg-[#fdfcf7] p-7 shadow-[0_1px_0_rgba(255,255,255,0.85)_inset,0_30px_80px_-20px_rgba(8,20,14,0.45),0_10px_30px_-12px_rgba(8,20,14,0.22)] sm:max-w-lg sm:p-9"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {/* Gifts spent: say so up front, so the modal doesn't keep promoting a Human-Made
            gift the artist can't give — it's back to the standard points invite. */}
        {subscriptionExhausted ? (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-sm font-medium text-amber-800"
            data-testid="invite-gifts-used-up"
          >
            You&apos;ve used all {allowance?.limit} of your Human-Made gifts — invites now gift{' '}
            {referralSettings.referralPoints} points instead.
          </div>
        ) : null}

        {/* The seal: a gift on a soft gradient disc with an emerald glow — the premium
            first read (matches the Get-Verified badge treatment). */}
        <div className="flex justify-center">
          <span
            className="flex h-[76px] w-[76px] items-center justify-center rounded-full bg-gradient-to-br from-emerald-100 via-emerald-50 to-amber-100 text-[2.9rem] shadow-[0_10px_28px_-8px_rgba(16,185,129,0.4)] ring-1 ring-emerald-200/60"
            aria-hidden="true"
          >
            🎁
          </span>
        </div>
        {/* Mono eyebrow — the editorial signal used across /verify and /points. */}
        <p className="flex items-center justify-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-700">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" aria-hidden="true" />
          Refer a friend
        </p>
        <DialogHeader className="gap-1">
          <DialogTitle className="text-center text-[1.6rem] font-bold leading-[1.12] tracking-[-0.01em] text-[#0a1020]">
            {copy.headlineMain}
          </DialogTitle>
          <p className="text-center text-lg font-bold text-red-600">{copy.headlineSub}</p>
        </DialogHeader>

        {/* Scarcity is the point — an artist gets only a few of these to give, so say how
            many are left. Once spent, the top notice takes over and this line drops away. */}
        {allowance && allowance.remaining > 0 ? (
          <p className="text-center text-xs font-medium text-gray-500" data-testid="invite-gift-allowance">
            You have {allowance.remaining} of {allowance.limit} gift{' '}
            {allowance.limit === 1 ? 'subscription' : 'subscriptions'} left to give.
          </p>
        ) : null}

        {/* Gift a SPECIFIC person: we email them, and it shows up on the referrals page where
            it can be resent or cancelled. Only when a subscription is on offer and a slot is free. */}
        {referralSettings.grantsSubscription && allowance && allowance.remaining > 0 ? (
          <div
            className="rounded-2xl border border-black/[0.06] bg-white p-2.5 shadow-[0_1px_2px_rgba(8,20,14,0.05),0_8px_24px_-16px_rgba(8,20,14,0.35)]"
            data-testid="invite-gift-directed"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                type="email"
                inputMode="email"
                placeholder="Gift a friend by email…"
                value={giftEmail}
                onChange={(e) => setGiftEmail(e.target.value)}
                className="min-w-0 flex-1 border-transparent bg-transparent shadow-none focus-visible:ring-0"
                data-testid="invite-gift-email"
              />
              <Button
                type="button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 sm:w-auto"
                disabled={sendingGift || !giftEmail.trim()}
                onClick={sendGift}
              >
                <Mail className="mr-1.5 h-4 w-4" aria-hidden="true" />
                {sendingGift ? 'Sending…' : 'Send gift'}
              </Button>
            </div>
          </div>
        ) : null}

        {/* When a directed gift is on offer, the link is the other way to invite — label the seam. */}
        {referralSettings.grantsSubscription && allowance && allowance.remaining > 0 ? (
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 border-t border-dashed border-black/10" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">
              or share your link
            </span>
            <span className="h-px flex-1 border-t border-dashed border-black/10" />
          </div>
        ) : null}

        {/* Monzo-style tracker — present even at zero, so the promise reads real. */}
        <p className="text-center text-xs font-medium text-gray-500" data-testid="invite-friend-stats">
          {stats
            ? stats.friendsJoined === 0
              ? 'No friends joined yet — be the first to share your link'
              : `${stats.friendsJoined} ${stats.friendsJoined === 1 ? 'friend has' : 'friends have'} joined · you've earned ${stats.pointsEarned.toLocaleString()} points`
            : ' '}
        </p>

        {nativeShare ? (
          <Button
            type="button"
            className="w-full rounded-full bg-emerald-600 py-5 text-base text-white shadow-[0_8px_20px_-8px_rgba(16,185,129,0.6)] hover:bg-emerald-700"
            data-testid="invite-friend-share"
            onClick={() => {
              void shareNative(message, link).then((shared) => {
                if (shared) share('native');
              });
            }}
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share invite
          </Button>
        ) : null}

        {/* One share action: the native sheet above (mobile), and a single Copy link that goes
            anywhere. The per-channel Email/Text/WhatsApp buttons were removed — they added noise
            and only worked if she happened to use that app (Nick, 2026-09-14). */}
        <button
          type="button"
          className={copyButton}
          data-testid="invite-friend-copy"
          onClick={(event) => {
            const { clientX, clientY } = event;
            void navigator.clipboard.writeText(link).then(() => {
              setCopied(true);
              // Confetti from the button she just pressed (Nick, 2026-09-01) —
              // canvas-confetti paints its own top-layer canvas, above the dialog.
              const origin = { x: clientX / window.innerWidth, y: clientY / window.innerHeight };
              void confetti({ particleCount: 90, spread: 75, startVelocity: 32, origin });
              void confetti({
                particleCount: 40,
                spread: 120,
                startVelocity: 20,
                scalar: 0.8,
                origin,
                colors: ['#16a34a', '#f59e0b', '#6366f1', '#ec4899'],
              });
              share('copy');
              amplitude.track('sn_referral_link_copied', {
                source,
                ...(promo?.kind === 'quest' ? { quest: promo.slug } : {}),
              });
              setTimeout(() => setCopied(false), 2000);
            });
          }}
        >
          {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
          {copied ? 'Copied!' : 'Copy link'}
        </button>

        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-semibold text-gray-500">
          <button
            type="button"
            className="inline-flex items-center gap-1 hover:text-gray-800"
            data-testid="invite-friend-qr-toggle"
            onClick={() => {
              setShowQr((prev) => {
                if (!prev) share('qr');
                return !prev;
              });
            }}
          >
            {showQr ? 'Hide QR code' : 'Show QR code'}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${showQr ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 hover:text-gray-800"
            data-testid="invite-friend-how-toggle"
            onClick={() => setShowHow((prev) => !prev)}
          >
            How it works
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${showHow ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        </div>

        {qrModules ? (
          <div className="flex justify-center" data-testid="invite-friend-qr">
            <svg
              viewBox={`0 0 ${qrModules.count} ${qrModules.count}`}
              className="h-40 w-40 rounded-lg border border-gray-200 bg-white p-2"
              role="img"
              aria-label="QR code for your invite link"
              shapeRendering="crispEdges"
            >
              {qrModules.modules.map((m) => (
                <rect key={`${m.x}-${m.y}`} x={m.x} y={m.y} width={1} height={1} fill="#111" />
              ))}
            </svg>
          </div>
        ) : null}

        {showHow ? (
          <ol
            className="mx-auto max-w-xs list-decimal space-y-1 pl-5 text-sm text-gray-600"
            data-testid="invite-friend-how"
          >
            <li>Share your link with a friend.</li>
            <li>{copy.howFriend}</li>
            <li>{copy.howYou}</li>
            <li>Join the same quest and you become quest buddies — finish it together and you each get 250 more.</li>
          </ol>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
